import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';

import {
    Box, Paper, Fab, Typography, CircularProgress, Container, Button
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

// Custom components and functions
import CustomStatusChip from '../../utils/components/CustomStatusChip';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import { getDate } from '../../utils/functions/Utils';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';

const TablaInventario = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState([{}]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState('');
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Almacen', 'Secretaria', 'Ayudante'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Movimientos de Inventario';
    }, [auth])

    const getData = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/inventories/');
            if (res.status === 200 && res.data.length > 0) {
                if (res.data.length <= 1) {
                    res.data[0].solicitante = res.data[0].entrada ? res.data[0].entrada.entrega : res.data[0].salida.solicitante;
                    res.data[0].fechaMovimiento = res.data[0].entrada ? getDate(res.data[0].entrada.fechaDeEntrada, 'DD H:mm:ss') : getDate(res.data[0].salida.fechaDeSalida, 'DD H:mm:ss');
                    res.data[0].clienteProveedor = res.data[0].entrada ? res.data[0].entrada.proveedor : res.data[0].salida.cliente;
                    res.data[0].noOrden = res.data[0].salida ?
                        res.data[0].operacion === 'Servicio' ? 'Reporte #' + res.data[0].salida.infoServicio.noReporte :
                            res.data[0].salida.noOrden : res.data[0].entrada.noFactura;
                    setData(res.data);
                }
                else {
                    const tmp = res.data.map((item) => {
                        item.solicitante = item.entrada ? item.entrada.entrega : item.salida.solicitante;
                        item.fechaMovimiento = item.entrada ? getDate(item.entrada.fechaDeEntrada, 'DD H:mm:ss') : getDate(item.salida.fechaDeSalida, 'DD H:mm:ss');
                        item.clienteProveedor = item.entrada ? item.entrada.proveedor : item.salida.cliente;
                        item.noOrden = item.salida ?
                            item.operacion === 'Servicio' ? 'Reporte #' + item.salida.infoServicio.noReporte :
                                item.salida.noOrden : item.entrada.noFactura;
                        return item;
                    });
                    setData(tmp);
                }
                setLoading(false);
                document.title = 'Movimientos de Inventario';
            } else {
                setData([]);
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            alert('Ocurrió un error al cargar los movimientos del inventario, favor de intentarlo mas tarde');
            setLoading(false);
        }
    }

    useEffect(() => {
        getData();
    }, [])

    const renderSalidas = () => {
        if (data.length === 0) return null;
        else {
            return (
                <div >
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                        <Button size='small' variant='outlined' onClick={() => navigate('/inventario')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                        <Typography variant='h5' align='left' sx={{ mt: 1, mb: 1, fontWeight: 700, ml: 3 }}>Movimientos de inventario</Typography>
                    </div>
                    <DataGrid
                        autoPageSize={true}
                        sx={{ '& .MuiDataGrid-toolbarContainer': { position: 'fixed', top: '70px', right: '24px' }, '& .MuiButtonBase-root': { color: 'comp.gray' }, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                        style={{ height: `${height - 120}px`, width: '100%', overflow: 'hidden' }}
                        localeText={LocaleTextDataGrid}
                        rows={data}
                        density='compact'
                        getRowId={(row) => row._id}
                        onCellClick={(e) => { setSelected(e.row._id) }}
                        onCellDoubleClick={(e) => { navigate(`/inventario/detalleMovimiento/${e.row._id}`, { target: '_blank' }) }}
                        columns={[
                            { field: 'noMovimiento', headerName: 'Consecutivo', flex: 1, minWidth: 60, maxWidth: 70, headerAlign: 'center', align: 'center' },
                            { field: 'solicitante', headerName: 'Solicita/Entrega', flex: 1, minWidth: 200, headerAlign: 'center', align: 'center' },
                            { field: 'noOrden', headerName: '# Cotización', align: 'center', headerAlign: 'center', flex: 1, minWidth: 200 },
                            {
                                field: 'estatus',
                                flex: 1,
                                minWidth: 200,
                                headerName: 'Estatus',
                                headerClassName: 'text-bold',
                                align: 'center',
                                headerAlign: 'center',
                                renderCell: (params) => (
                                    <CustomStatusChip variant='outlined' estatus={params.row.estatus} label={params.row.estatus} />
                                ),
                            },
                            {
                                field: 'tipoMovimiento',
                                flex: 1,
                                headerName: 'Tipo de Movimiento',
                                align: 'center',
                                headerAlign: 'center',
                                minWidth: 200,
                                renderCell: (params) => (
                                    <CustomStatusChip estatus={params.row.tipoMovimiento} label={params.row.tipoMovimiento} />
                                ),
                            },
                            {
                                field: 'motivo',
                                flex: 1,
                                headerName: 'Motivo',
                                align: 'center',
                                headerAlign: 'center',
                                minWidth: 200,
                                renderCell: (params) => (
                                    <CustomStatusChip estatus={params.row.motivo} label={params.row.motivo} />
                                ),
                            },
                            { field: 'auth', flex: 1, headerName: 'Quien autoriza', align: 'center', headerAlign: 'center', minWidth: 200 },
                            { field: 'clienteProveedor', flex: 1, headerName: 'Cliente/Proveedor', align: 'center', headerAlign: 'center', minWidth: 200 },
                            { field: 'fechaMovimiento', flex: 1, headerName: 'Fecha de Movimiento', align: 'center', headerAlign: 'center', minWidth: 200 },
                        ]}
                        pagination
                        slots={{
                            pagination: CustomPagination,
                            toolbar: GridToolbar
                        }}
                        initialState={{
                            ...data.initialState,
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                    />
                </div>
            )
        }
    }

    return (
        <Container maxWidth='xxl' sx={{ width: '100%', overflow: 'hidden', margin: '0px', padding: '4px 4px !important' }}>
            <Paper>
                {loading ?
                    <Box style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '128px' }}>
                        <CircularProgress size='5em' color='primary' />
                    </Box> :

                    <>
                        {selected !== '' ?
                            <div>
                                <Link target='_blank' to={{ pathname: '/inventario/detalleMovimiento/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                                    <Fab aria-label="add" color='warning' style={{ position: 'fixed', bottom: '8px', left: '8px', zIndex: '10', color: '#FFF' }}>
                                        <i className="fad fa-edit fa-2x"></i>
                                    </Fab>
                                </Link>
                            </div>
                            : null}
                        {renderSalidas()}
                    </>
                }
            </Paper>
        </Container>
    );
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaInventario;